import { Team, WorkingHours } from "../types/core";

export const sortTeams = (t1: Team, t2: Team) => t1.name.localeCompare(t2.name);

/** The server keeps at most this many team slots per company. */
export const MAX_TEAMS = 99;

/**
 * The number for a new team: the lowest free one that was never deleted, so a new team can't
 * inherit what still points at an old one. Deleted numbers come back only once nothing else is free.
 * Undefined when all slots are taken.
 */
export function pickNewTeamId(teamIds: number[], deletedIds: number[] = [], max = MAX_TEAMS): number | undefined {
    const used = new Set(teamIds);
    const deleted = new Set(deletedIds);
    for (let id = 1; id <= max; id++) {
        if (!used.has(id) && !deleted.has(id)) {
            return id;
        }
    }
    for (let id = 1; id <= max; id++) {
        if (!used.has(id)) {
            return id;
        }
    }
    return undefined;
}

const WEEK: (keyof Omit<WorkingHours, "source">)[] = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function hhmm(value: number, amPm: boolean) {
    const hours = Math.floor(value / 100);
    const minutes = String(value % 100).padStart(2, "0");
    if (amPm) {
        // 2400 is the end of the day: midnight, like 0000
        return `${hours % 12 || 12}:${minutes} ${hours % 24 < 12 ? "AM" : "PM"}`;
    }
    return String(hours).padStart(2, "0") + ":" + minutes;
}

/**
 * Weekly hours in a few words, days with the same window run together: "Mon–Fri 08:00–17:00".
 * Windows are HHMM numbers (800 = 08:00); 0–0 is a day off. `dayNames` starts on Monday.
 * `amPm` writes the times the way the account reads them: "8:00 AM–5:00 PM"; `allDay` replaces 00:00–24:00.
 */
export function summarizeHours(hours: WorkingHours | undefined, dayNames: string[], amPm = false, allDay?: string): string {
    if (!hours) {
        return "";
    }
    const parts: string[] = [];
    let i = 0;
    while (i < WEEK.length) {
        const window = hours[WEEK[i]];
        if (!window || (window.start === 0 && window.end === 0)) {
            i++;
            continue;
        }
        let j = i;
        while (j + 1 < WEEK.length && hours[WEEK[j + 1]]?.start === window.start && hours[WEEK[j + 1]]?.end === window.end) {
            j++;
        }
        const days = i === j ? dayNames[i] : `${dayNames[i]}–${dayNames[j]}`;
        const times = allDay && window.start === 0 && window.end >= 2400 ? allDay : `${hhmm(window.start, amPm)}–${hhmm(window.end, amPm)}`;
        parts.push(`${days} ${times}`);
        i = j + 1;
    }
    return parts.join(", ");
}
